import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card, Table, Tag, Button, Space, Row, Col,
    message, Typography, Avatar, Empty, DatePicker, Progress
} from 'antd';
import {
    ClockCircleOutlined, CheckCircleOutlined,
    TeamOutlined, CrownOutlined, ReloadOutlined, EyeOutlined,
    WalletOutlined, CalendarOutlined, CarOutlined
} from '@ant-design/icons';
import affiliatesService from '../../common/services/affiliates.service';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import locale from 'antd/es/date-picker/locale/fr_FR';

dayjs.locale('fr');

const { Title, Text } = Typography;

const Commissions130hPage = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [beneficiaries, setBeneficiaries] = useState([]);
    const [selectedMonth, setSelectedMonth] = useState(dayjs());
    const [stats, setStats] = useState({
        totalBeneficiaries: 0,
        totalAffiliates: 0,
        totalAmbassadors: 0,
        totalDriversReached130h: 0,
        totalPotentialAmount: 0
    });

    const fetchBeneficiaries = useCallback(async () => {
        setLoading(true);
        try {
            const response = await affiliatesService.get130hBeneficiaries({
                month: selectedMonth.format('YYYY-MM')
            });

            if (response.success) {
                setBeneficiaries(response.data || []);
                if (response.stats) {
                    setStats(response.stats);
                }
            }
        } catch (error) {
            message.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    }, [selectedMonth]);

    useEffect(() => { fetchBeneficiaries(); }, [fetchBeneficiaries]);

    const handleMonthChange = (date) => {
        if (date) {
            setSelectedMonth(date);
        }
    };

    const columns = [
        {
            title: 'Bénéficiaire',
            key: 'beneficiary',
            render: (_, record) => {
                const isAmbassador = record.recipientType === 'ambassador';
                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <Avatar
                            size={44}
                            style={{ background: isAmbassador ? '#722ed1' : '#1890ff', flexShrink: 0 }}
                            icon={isAmbassador ? <CrownOutlined /> : <TeamOutlined />}
                        />
                        <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 600, color: '#262626', fontSize: 15 }}>
                                {record.firstName} {record.lastName}
                            </div>
                            <div style={{ fontSize: 12, color: '#8c8c8c' }}>{record.phone}</div>
                            <Tag
                                color={isAmbassador ? 'purple' : 'blue'}
                                style={{ marginTop: 4, borderRadius: 10, fontSize: 11 }}
                            >
                                {isAmbassador ? 'Ambassadeur' : 'Affilié'}
                            </Tag>
                        </div>
                    </div>
                );
            }
        },
        {
            title: 'Chauffeurs',
            key: 'drivers',
            width: 120,
            render: (_, record) => (
                <div style={{ textAlign: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                        <CarOutlined style={{ color: '#1890ff' }} />
                        <span style={{ fontSize: 18, fontWeight: 600, color: '#262626' }}>
                            {record.driversCount || 0}
                        </span>
                    </div>
                    <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                        chauffeur{(record.driversCount || 0) > 1 ? 's' : ''}
                    </div>
                </div>
            )
        },
        {
            title: 'Heures totales',
            key: 'totalHours',
            width: 140,
            render: (_, record) => (
                <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 16, fontWeight: 600, color: '#fa8c16' }}>
                        {record.totalHoursThisMonth || 0}h
                    </div>
                    <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                        ce mois
                    </div>
                </div>
            )
        },
        {
            title: '130h atteint',
            key: 'reached130h',
            width: 130,
            render: (_, record) => (
                <div style={{ textAlign: 'center' }}>
                    {record.driversReached130h > 0 ? (
                        <>
                            <Tag color="green" style={{ borderRadius: 10, fontSize: 14, padding: '2px 12px' }}>
                                <CheckCircleOutlined style={{ marginRight: 4 }} />
                                {record.driversReached130h}
                            </Tag>
                            <div style={{ fontSize: 11, color: '#52c41a', marginTop: 4 }}>
                                chauffeur{record.driversReached130h > 1 ? 's' : ''}
                            </div>
                        </>
                    ) : (
                        <Tag color="default" style={{ borderRadius: 10 }}>
                            <ClockCircleOutlined style={{ marginRight: 4 }} />
                            0
                        </Tag>
                    )}
                </div>
            )
        },
        {
            title: 'Commission potentielle',
            key: 'amount',
            width: 150,
            render: (_, record) => (
                <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 18, fontWeight: 700, color: record.potentialAmount > 0 ? '#52c41a' : '#bfbfbf' }}>
                        {(record.potentialAmount || 0).toLocaleString()} <span style={{ fontSize: 12 }}>F</span>
                    </div>
                    {record.commissions130hCount > 0 && (
                        <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                            {record.commissions130hCount} commission{record.commissions130hCount > 1 ? 's' : ''} créée{record.commissions130hCount > 1 ? 's' : ''}
                        </div>
                    )}
                </div>
            )
        },
        {
            title: '',
            key: 'actions',
            width: 50,
            render: (_, record) => (
                <Button
                    type="text"
                    icon={<EyeOutlined />}
                    onClick={() => {
                        navigate(`/dashboard/commissions-130h/${record.recipientType}/${record._id}?month=${selectedMonth.format('YYYY-MM')}`);
                    }}
                    style={{ color: '#1890ff' }}
                />
            )
        }
    ];

    // Stats Card Component
    const StatCard = ({ icon, iconBg, title, value, suffix, subtitle }) => (
        <Card
            style={{
                borderRadius: 12,
                height: '100%'
            }}
            bodyStyle={{ padding: 16 }}
        >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <div style={{
                    width: 44,
                    height: 44,
                    borderRadius: 10,
                    background: iconBg,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                }}>
                    {icon}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>{title}</div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: '#262626', lineHeight: 1.2 }}>
                        {typeof value === 'number' ? value.toLocaleString() : value}
                        {suffix && <span style={{ fontSize: 14, fontWeight: 500, marginLeft: 4 }}>{suffix}</span>}
                    </div>
                    {subtitle && <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 2 }}>{subtitle}</div>}
                </div>
            </div>
        </Card>
    );

    return (
        <div style={{ padding: 16, maxWidth: 1400, margin: '0 auto' }}>
            {/* Header */}
            <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 24,
                gap: 12
            }}>
                <div>
                    <Title level={4} style={{ margin: 0, color: '#262626' }}>
                        Commission 130h Mensuel
                    </Title>
                    <Text type="secondary" style={{ fontSize: 13 }}>
                        Suivi des heures mensuelles des chauffeurs pour les commissions 130h
                    </Text>
                </div>
                <Space>
                    <DatePicker
                        picker="month"
                        value={selectedMonth}
                        onChange={handleMonthChange}
                        format="MMMM YYYY"
                        style={{ borderRadius: 8 }}
                        allowClear={false}
                        locale={locale}
                    />
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={() => fetchBeneficiaries()}
                        style={{ borderRadius: 8 }}
                    >
                        Actualiser
                    </Button>
                </Space>
            </div>

            {/* Stats Cards */}
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={12} sm={6}>
                    <StatCard
                        icon={<TeamOutlined style={{ fontSize: 20, color: '#1890ff' }} />}
                        iconBg="#e6f7ff"
                        title="Affiliés"
                        value={stats.totalAffiliates}
                    />
                </Col>
                <Col xs={12} sm={6}>
                    <StatCard
                        icon={<CrownOutlined style={{ fontSize: 20, color: '#722ed1' }} />}
                        iconBg="#f9f0ff"
                        title="Ambassadeurs"
                        value={stats.totalAmbassadors}
                    />
                </Col>
                <Col xs={12} sm={6}>
                    <StatCard
                        icon={<CheckCircleOutlined style={{ fontSize: 20, color: '#52c41a' }} />}
                        iconBg="#f6ffed"
                        title="Chauffeurs 130h"
                        value={stats.totalDriversReached130h}
                        subtitle="ont atteint 130h"
                    />
                </Col>
                <Col xs={24} sm={6}>
                    <Card
                        style={{
                            borderRadius: 12,
                            background: 'linear-gradient(135deg, #fa8c16 0%, #ff7a45 100%)',
                            border: 'none',
                            height: '100%'
                        }}
                        bodyStyle={{ padding: 16 }}
                    >
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                            <div style={{
                                width: 44,
                                height: 44,
                                borderRadius: 10,
                                background: 'rgba(255,255,255,0.2)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <WalletOutlined style={{ fontSize: 20, color: '#fff' }} />
                            </div>
                            <div>
                                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.8)', marginBottom: 4 }}>Potentiel 130h</div>
                                <div style={{ fontSize: 22, fontWeight: 700, color: '#fff', lineHeight: 1.2 }}>
                                    {stats.totalPotentialAmount?.toLocaleString() || 0}
                                    <span style={{ fontSize: 14, fontWeight: 500, marginLeft: 4 }}>F</span>
                                </div>
                            </div>
                        </div>
                    </Card>
                </Col>
            </Row>

            {/* Info mois */}
            <Card
                style={{
                    borderRadius: 12,
                    marginBottom: 16,
                    background: '#fff7e6',
                    border: '1px solid #ffd591'
                }}
                bodyStyle={{ padding: '12px 16px' }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <CalendarOutlined style={{ fontSize: 18, color: '#fa8c16' }} />
                    <div>
                        <Text strong style={{ color: '#d46b08' }}>
                            Période: {selectedMonth.format('MMMM YYYY')}
                        </Text>
                        <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                            Les commissions 130h sont calculées mensuellement. Cliquez sur un bénéficiaire pour voir la progression de ses chauffeurs.
                        </Text>
                    </div>
                </div>
            </Card>

            {/* Table */}
            <Card style={{ borderRadius: 12 }} bodyStyle={{ padding: 0 }}>
                <div style={{
                    padding: '16px 20px',
                    borderBottom: '1px solid #f0f0f0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <Text strong style={{ fontSize: 15 }}>
                        Liste des bénéficiaires
                    </Text>
                    <Text type="secondary" style={{ fontSize: 13 }}>
                        {beneficiaries.length} bénéficiaire{beneficiaries.length > 1 ? 's' : ''}
                    </Text>
                </div>

                <Table
                    columns={columns}
                    dataSource={beneficiaries}
                    rowKey="_id"
                    loading={loading}
                    pagination={{
                        pageSize: 15,
                        showSizeChanger: true,
                        pageSizeOptions: ['10', '15', '25', '50'],
                        size: 'small',
                        style: { marginRight: 16 }
                    }}
                    locale={{
                        emptyText: (
                            <Empty
                                description="Aucun bénéficiaire trouvé"
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                            />
                        )
                    }}
                    size="middle"
                />
            </Card>
        </div>
    );
};

export default Commissions130hPage;
